import { useEffect, useMemo, useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { Heart, Search, X } from "lucide-react";
import { NavLink, useLocation } from "react-router-dom";
import { IS_API_MODE } from "../../shared/config/runtime";
import { listPosts, getCursorFromPageUrl } from "../../entities/post/api";
import { getApiErrorMessage } from "../../shared/api/errors";
import { adaptPost, normalizeCursorPage } from "../../entities/post/adapter";
import { exploreWorks } from "../../shared/data/mockData";
import PosterArtwork from "../../entities/post/ui/PosterArtwork";

const numberFormat = new Intl.NumberFormat("zh-CN");
const portraitCardRatio = 1.18;
const exploreCategories = [
  { value: "", label: "为你推荐" },
  { value: "POSTER", label: "海报设计" },
  { value: "BRAND_VISUAL", label: "品牌视觉" },
  { value: "PROMOTION", label: "宣传图" },
  { value: "TYPOGRAPHY", label: "字体设计" },
  { value: "PACKAGING", label: "包装设计" },
];

function ExplorePostCard({ post, backgroundLocation }) {
  const [isPortrait, setIsPortrait] = useState(false);

  function detectCoverRatio(event) {
    const image = event.currentTarget;
    if (!image.naturalWidth || !image.naturalHeight) return;
    setIsPortrait(image.naturalHeight / image.naturalWidth >= portraitCardRatio);
  }

  return (
    <NavLink
      className={isPortrait ? "featured" : ""}
      to={`/posts/${post.id}`}
      state={{ backgroundLocation }}
      aria-label={`查看${post.author}的作品`}
    >
      {post.image ? (
        <img
          src={post.image}
          alt={`${post.author}发布的设计作品`}
          onLoad={detectCoverRatio}
        />
      ) : (
        <PosterArtwork artwork={post.artwork} compact />
      )}
      <span>
        <Heart size={18} fill="currentColor" /> {numberFormat.format(post.likes)}
      </span>
    </NavLink>
  );
}

export default function ExplorePage() {
  const location = useLocation();
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [category, setCategory] = useState("");

  useEffect(() => {
    const timeoutId = window.setTimeout(() => setDebouncedQuery(query.trim()), 300);
    return () => window.clearTimeout(timeoutId);
  }, [query]);

  const exploreQuery = useInfiniteQuery({
    queryKey: ["posts", "explore", category, debouncedQuery],
    queryFn: ({ pageParam }) =>
      listPosts({
        cursor: pageParam,
        pageSize: 24,
        postType: category,
        search: debouncedQuery,
      }),
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => getCursorFromPageUrl(lastPage?.next),
    enabled: IS_API_MODE,
  });

  const remotePosts = useMemo(
    () =>
      exploreQuery.data?.pages.flatMap((page) =>
        normalizeCursorPage(page).items.map(adaptPost),
      ) || [],
    [exploreQuery.data],
  );

  const mockWorks = useMemo(() => {
    if (!query.trim()) return exploreWorks;
    const normalizedQuery = query.trim().toLocaleLowerCase("zh-CN");
    return exploreWorks.filter((artwork) =>
      [artwork.title, artwork.eyebrow, artwork.subtitle]
        .filter(Boolean)
        .some((value) => value.toLocaleLowerCase("zh-CN").includes(normalizedQuery)),
    );
  }, [query]);

  return (
    <section className="page-container explore-page">
      <label className="search-box">
        <Search size={20} />
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="搜索设计师、作品或风格"
        />
        {query && (
          <button onClick={() => setQuery("")} aria-label="清空搜索">
            <X size={17} />
          </button>
        )}
      </label>
      <div className="topic-pills">
        {exploreCategories.map((item) => (
            <button
              className={category === item.value ? "active" : ""}
              key={item.value || "all"}
              onClick={() => setCategory(item.value)}
            >
              {item.label}
            </button>
          ))}
      </div>
      {exploreQuery.isPending && IS_API_MODE && (
        <div className="explore-skeleton" aria-label="正在加载设计作品">
          {Array.from({ length: 9 }, (_, index) => <span key={index} />)}
        </div>
      )}
      {exploreQuery.isError && (
        <div className="feed-state error">
          <span>{getApiErrorMessage(exploreQuery.error, "设计作品加载失败")}</span>
          <button onClick={() => exploreQuery.refetch()}>重新加载</button>
        </div>
      )}
      {!exploreQuery.isPending && IS_API_MODE && remotePosts.length === 0 && (
        <div className="explore-empty">
          <strong>{debouncedQuery || category ? "没有找到匹配的作品" : "还没有公开作品"}</strong>
          <p>{debouncedQuery || category ? "尝试更换关键词或设计分类。" : "发布第一件平面设计作品，让灵感被看见。"}</p>
          <NavLink className="secondary-button" to="/create">发布作品</NavLink>
        </div>
      )}
      <div className="explore-grid">
        {IS_API_MODE
          ? remotePosts.map((post) => (
              <ExplorePostCard
                key={post.id}
                post={post}
                backgroundLocation={location}
              />
            ))
          : mockWorks.map((artwork, index) => (
              <button key={`${artwork.title}-${index}`}>
                <PosterArtwork artwork={artwork} compact />
                <span>
                  <Heart size={18} fill="currentColor" /> {numberFormat.format(320 + index * 183)}
                </span>
              </button>
            ))}
      </div>
      {exploreQuery.hasNextPage && (
        <button
          className="load-more-button explore-load-more"
          disabled={exploreQuery.isFetchingNextPage}
          onClick={() => exploreQuery.fetchNextPage()}
        >
          {exploreQuery.isFetchingNextPage ? "正在加载更多作品……" : "加载更多作品"}
        </button>
      )}
    </section>
  );
}
